import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { GuidedTourService, Orientation } from 'ngx-guided-tour'

import { appConstants } from '../../app.constants'
import { IncentiveMode } from '../../shared/enums/incentive-mode.enum'
import { BreadcrumbLink } from '../../shared/interfaces/resources/breadcrumb-link.interface'
import { Field } from '../../shared/interfaces/resources/field.interface'
import { Filter } from '../../shared/interfaces/resources/filter.interface'
import { Incentive } from '../../shared/interfaces/resources/incentive.interface'
import { Theme } from '../../shared/interfaces/resources/theme.interface'
import { BreadcrumbService } from '../../shared/services/breadcrumb.service'
import { FavoriteService } from '../../shared/services/favorite.service'
import { FilterService } from '../../shared/services/filter.service'
import { FlashMessageService } from '../../shared/services/flash-message.service'
import { IncentiveService } from '../../shared/services/incentive.service'
import { MetaService } from '../../shared/services/meta.service'
import { ThemeService } from '../../shared/services/theme.service'
import { IncentiveFamily } from '../../shared/enums/incentive.family'
import { Subject, takeUntil } from 'rxjs'
import { IncentiveFamilyService } from '../../shared/services/incentive-family.service'

@Component({
  selector: 'app-incentive-database',
  templateUrl: './incentive-database.component.html',
  styleUrls: ['./incentive-database.component.scss']
})
export class IncentiveDatabaseComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer: ElementRef
  @ViewChild('scrollBarWrapper') scrollBarWrapper: ElementRef
  @ViewChild('scrollBarChild') scrollBarChild: ElementRef

  breadcrumbLinks: BreadcrumbLink[]
  incentives: Incentive[]

  loadingIncentives = true
  loadingFilters = true
  loadingDownload = false

  filters: Filter[]
  tableColumns: { label: string; fieldId: string }[]
  showAdvancedFilters: boolean = false
  showExportMenu: boolean = false
  activeOptions: { value: string; filterId: string }[] = []
  isAllFavorites: boolean

  basePath = 'incentives'
  selectedFamily: IncentiveFamily;
  families = this.incentiveFamilyService.families
  introText = ''

  private readonly destroy$ = new Subject<void>()
  private loadSequence = 0

  form: UntypedFormGroup = this.formBuilder.group({
    similarTo: new UntypedFormControl()
  })

  constructor(
    breadcrumbService: BreadcrumbService,
    private metaService: MetaService,
    private incentiveService: IncentiveService,
    private favoriteService: FavoriteService,
    private filterService: FilterService,
    private themeService: ThemeService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private flashMessageService: FlashMessageService,
    private guidedTourService: GuidedTourService,
    private incentiveFamilyService: IncentiveFamilyService
  ) {
    breadcrumbService.breadcrumbLinks.next([{ label: 'Find incentives' }])
  }

  ngOnInit(): void {
    this.favoriteService.favoriteIncentives
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAllFavoritesState())

    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((queryParams: Params) => {
        void this.loadContent(queryParams)
      })

    this.metaService.setTags('incentives')
  }

  private async loadContent(queryParams: Params): Promise<void> {
      const loadSequence = ++this.loadSequence
      this.loadingFilters = true
      this.loadingIncentives = true
      this.tableColumns = undefined

      // Set the active options for each filter.
      this.activeOptions = []

      // Special case for similarTo as it has no filter thus no options predefined.
      if (queryParams['similarTo']) {
        this.activeOptions.push({
          value: (await this.incentiveService.show(queryParams['similarTo']))
            ?.label,
          filterId: 'similarTo'
        })
      }

      if (queryParams['dataset'] && queryParams['dataset'] == IncentiveFamily.IPTAX.valueOf()) {
        this.selectedFamily = IncentiveFamily.IPTAX
      } else {
        this.selectedFamily = IncentiveFamily.RDTAX
      }
      this.incentiveFamilyService.setFamily(this.selectedFamily)

      this.filters = await this.filterService.list({
        dataset: this.selectedFamily
      }).then(
        (filterRes: Filter[]) => {
          if (loadSequence === this.loadSequence) {
            this.loadingFilters = false
          }

          this.introText = filterRes[0]?.introText ?? '';

          // Sort filters, type "basic" are shown first.
          return filterRes.sort((a: Filter, b: Filter) => {
            if (a.type === 'basic' && b.type !== 'basic') {
              return -1
            }
            if (a.type !== 'basic' && b.type === 'basic') {
              return 1
            }
            return 0
          })
        },
        () => {
          if (loadSequence === this.loadSequence) {
            this.loadingFilters = false
            this.flashMessageService.error(
              'Could not load filters. Please try again later.'
            )
          }
          return []
        }
      )

      if (loadSequence !== this.loadSequence) return

      Object.keys(this.form.controls)
        .filter((controlName) => controlName !== 'similarTo')
        .forEach((controlName) => this.form.removeControl(controlName))

      this.filters.forEach((filter: Filter) => {
        this.form.addControl(filter.fieldId, new UntypedFormControl())
      })

      this.filters.forEach((filter: Filter) => {
        if (queryParams[filter.fieldId]) {
          filter.activeOptions = queryParams[filter.fieldId]

          if (Array.isArray(queryParams[filter.fieldId])) {
            filter.activeOptions = queryParams[filter.fieldId]
            queryParams[filter.fieldId].forEach((value: string) => {
              this.activeOptions.push({
                value,
                filterId: filter.fieldId
              })
            })
          } else {
            filter.activeOptions = [queryParams[filter.fieldId]]
            this.activeOptions.push({
              value: queryParams[filter.fieldId],
              filterId: filter.fieldId
            })
          }
        } else {
          filter.activeOptions = []
        }
      })

      Object.keys(queryParams)
        .filter((key) => key !== 'reload')
        .forEach((fieldId) => {
          const field = this.form.get(fieldId);
          if (field) {
            field.setValue(queryParams[fieldId])
          }
        })

      this.filterService.saveFilters(this.filters)

      this.incentiveService
        .listWithoutPagination({
          fields: queryParams,
          mode: IncentiveMode.List
        })
        .then(
          async (incentiveRes: Incentive[]) => {
            if (loadSequence !== this.loadSequence) return
            this.loadingIncentives = false
            this.incentives = incentiveRes

            // Check and subscribe to check the mass selection checkbox if all incentives are in favorites.
            this.updateAllFavoritesState()

            if (this.incentives.length > 0) {
              this.tableColumns = await this.getTableColumns(
                this.incentives[0].fields.flat()
              )
              if (loadSequence !== this.loadSequence) return
            }

            this.guidedTourService?.startTour({
              tourId: 'IncentiveListTour',
              minimumScreenSize: appConstants.windowBreakpoints.desktop,
              useOrb: true,
              steps: [
                {
                  title: 'Welcome to the INNOTAX tour guide',
                  selector: '#incentive-database-h1',
                  content:
                    'Discover in 7 steps how you can find and compare tax incentives for R&D and innovation in this portal.',
                  orientation: Orientation.Center
                },
                {
                  selector: '#incentive-database-family-tabs',
                  title: 'Select type of incentives',
                  content:
                    'Choose whether you would like to explore expenditure-based or income-based tax incentives.',
                  orientation: Orientation.Bottom
                },
                {
                  selector: '.multiselect-box',
                  title: 'Find tax incentives',
                  content:
                    'Use filters to find incentives in certain countries or with specific features.',
                  orientation: Orientation.Right
                },
                {
                  selector: '.incentive-id',
                  title: 'Description of tax incentives',
                  content:
                    'Click on the name of the tax incentive to view its features.',
                  orientation: Orientation.Top
                },
                {
                  selector: '#favorite-button',
                  title: 'Select tax incentives for comparison',
                  content:
                    'Use the checkbox to select tax incentives for comparison.',
                  orientation: Orientation.Right
                },
                {
                  selector: '#my-comparator',
                  title: 'Comparison preview',
                  content:
                    'See the selection of tax incentives and click the button to compare their features.',
                  orientation: Orientation.Bottom
                },
                {
                  selector: '.dropdown-trigger',
                  title: 'Export as CSV',
                  content:
                    'Export the selected or full list of tax incentives, including description of design features.',
                  orientation: Orientation.Left
                }
              ]
            })

            setTimeout(() => {
              if (this.incentives.length > 0) {
                this.renderer.setStyle(
                  this.scrollBarChild?.nativeElement,
                  'width',
                  this.tableContainer?.nativeElement?.scrollWidth + 'px'
                )
              }
            }, 50)
          },
          () => {
            if (loadSequence !== this.loadSequence) return
            this.loadingIncentives = false
            this.flashMessageService.error(
              'Could not load incentives. Please try again later.'
            )
          }
        )
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.guidedTourService.resetTour()
  }

  private updateAllFavoritesState(): void {
    this.isAllFavorites = !!this.incentives?.length && this.incentives.every(
      (incentive) => this.favoriteService.isFavorite(incentive.id)
    )
  }

  clearAllForm() {
    // A bit hacky, but it works: reset filters and incentives.
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigate([this.basePath]))
  }

  clearOption(filterId: string, value: string) {
    const queryParams: { [key: string]: any } = Object.assign(
      {},
      this.activatedRoute.snapshot.queryParams
    )

    if (Array.isArray(queryParams[filterId])) {
      const index = queryParams[filterId].indexOf(value)
      if (index > -1) {
        queryParams[filterId].splice(index, 1)
      }
    } else {
      queryParams[filterId] = null
    }

    queryParams['reload'] = new Date().getTime()

    this.router.navigate([this.basePath], {
      queryParams,
      queryParamsHandling: 'merge'
    })
  }

  onFilterChange(fieldId: string, value: string[]) {
    const queryParams: any = {
      [fieldId]: value
    }

    this.router.navigate([this.basePath], {
      queryParams,
      queryParamsHandling: 'merge'
    })
  }

  isFavorite(incentive: Incentive): boolean {
    return this.favoriteService.isFavorite(incentive)
  }

  toggleAllFavorites(incentives: Incentive[]) {
    this.isAllFavorites = incentives.every((incentive) =>
      this.favoriteService.isFavorite(incentive)
    )

    if (this.isAllFavorites) {
      this.favoriteService.remove(
        incentives.map((i) => ({
          id: i.id,
          family: this.selectedFamily
        }))
      )
    } else {
      this.favoriteService.add(incentives, this.selectedFamily)
    }
  }

  onScroll(event: any, scrollbarName: 'top' | 'bottom') {
    if (scrollbarName === 'top') {
      this.tableContainer.nativeElement.scrollLeft = event.target.scrollLeft
    }
    if (scrollbarName === 'bottom') {
      this.scrollBarWrapper.nativeElement.scrollLeft = event.target.scrollLeft
    }
  }

  async getTableColumns(
    incentiveFields: Field[]
  ): Promise<{ label: string; fieldId: string }[]> {
    const themes: Theme[] = await this.themeService.list({
      mode: IncentiveMode.List,
      dataset: this.selectedFamily
    })

    // Get all field from the themes and subThemes.
    const themeFields = themes.flatMap(theme => theme.fields ?? []);

    const subThemeFields = themes.flatMap(theme =>
      theme.subThemes?.flatMap(subTheme => subTheme.fields ?? []) ?? []
    );

    return [...themeFields, ...subThemeFields]
      .filter(theme => theme)
      .filter(theme => incentiveFields.some(({ id }) => theme.id === id))
      .map(theme => ({
        label: theme.label,
        fieldId: theme.id
      }));
  }

  async downloadIncentives(incentives: Incentive[]): Promise<void> {
    this.loadingDownload = true
    await this.incentiveService.download(
      incentives.map((incentive) => incentive.id),
      this.selectedFamily
    )
    this.loadingDownload = false
  }

  changeFamily(family: IncentiveFamily): void {
    if (this.selectedFamily === family) {
      return
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        dataset: family
      }
    })
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: any) {
    if (
      this.showExportMenu &&
      !event.target.className.includes('dropdown-menu') &&
      !event.target.className.includes('button') &&
      !event.target.className.includes('is-hidden-touch')
    ) {
      this.showExportMenu = false
    }
  }
}
